import React, { useState, useEffect } from 'react';
import { Dialog, Spinner } from '@blueprintjs/core';
import { Button, TextAreaLimited } from 'components';
import { useMutation, useLazyQuery } from '@apollo/client';
import { APPROVE_DATA_FIX_MUTATION, DATA_FIX_REQUEST_PREVIEW_QUERY } from 'gql';
import { useModal } from 'providers/modalProvider';
import { useToast } from 'providers/toastProvider';
import { useIntl } from 'react-intl';
import { path } from 'ramda';
import { buildActionSummary, buildPreviewCount } from 'utils/dataFixSummary';

const ApproveDataFixModal = () => {
  const { modalDispatch, modalState } = useModal();
  const { showToast } = useToast();
  const { formatMessage } = useIntl();
  const [notes, setNotes] = useState('');

  const { row, refetch, onSuccess } = path(['payload'], modalState) ?? {};

  const closeModal = () => modalDispatch({ type: 'CLOSE_ALL' });

  const [loadPreview, { data: previewData, loading: previewLoading, error: previewError }] =
    useLazyQuery(DATA_FIX_REQUEST_PREVIEW_QUERY, {
      fetchPolicy: 'network-only',
    });

  useEffect(() => {
    if (modalState?.open && row?.id) {
      loadPreview({ variables: { id: row.id } });
    }
  }, [modalState?.open, row?.id, loadPreview]);

  const [approve, { loading: approving }] = useMutation(
    APPROVE_DATA_FIX_MUTATION,
    {
      onCompleted: () => {
        showToast({
          message: formatMessage({
            id: 'containers.dataFixes.modals.approve.success',
          }),
        });
        closeModal();
        refetch?.();
        onSuccess?.();
      },
      onError: () => {
        showToast({
          message: formatMessage({ id: 'containers.all.error' }),
          icon: 'error',
        });
        closeModal();
      },
    },
  );

  const handleApprove = () =>
    approve({
      variables: { input: { id: row.id, notes: notes || undefined } },
    });

  return (
    <Dialog
      isOpen={modalState?.open}
      title={formatMessage({ id: 'containers.dataFixes.modals.approve.title' })}
      onClose={closeModal}
      canOutsideClickClose={false}
    >
      <div className="bp3-dialog-body">
        <p>
          <strong>{buildActionSummary(row)}</strong>
        </p>
        <p>
          {previewLoading ? (
            <Spinner size={16} />
          ) : previewError ? (
            <span className="color-danger">
              {formatMessage({ id: 'containers.dataFixes.modals.approve.previewError' })}
            </span>
          ) : (
            buildPreviewCount(previewData?.dataFixRequestPreview)
          )}
        </p>
        <TextAreaLimited
          onChange={(e) => setNotes(e.target.value)}
          value={notes}
          placeholder={formatMessage({
            id: 'containers.dataFixes.modals.approve.notes',
          })}
          maxLength="500"
          showMaxLength
          className="mgt-10"
        />
      </div>
      <div className="bp3-dialog-footer-actions">
        <Button
          outlined
          text={formatMessage({ id: 'containers.all.btn.cancel' })}
          onClick={closeModal}
          disabled={approving}
        />
        <Button
          intent="Success"
          text={formatMessage({ id: 'containers.dataFixes.actions.approve' })}
          onClick={handleApprove}
          loading={approving}
          disabled={previewLoading}
        />
      </div>
    </Dialog>
  );
};

export default ApproveDataFixModal;
